import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Col, Collapse, Dropdown, Modal, Row, Spin, message } from "antd";
import type { CollapseProps } from "antd";
import { CaretRightOutlined, MoreOutlined, UploadOutlined } from "@ant-design/icons";
import {
	SubmitButtonOverlay,
	ActionContainer,
	ActionItemsContainer,
	FormHeaderComponent,
	InfoMessageBox,
	StatusCell,
} from "../../components/styled/common";
import BackComponent from "../../components/BackComponent";
import CollapseExpandIcon from "../../components/CollapseExpandIcon";
import GalleryMediaPicker from "../../components/GalleryMediaPicker";
import InputComponent from "../../components/InputComponent";
import InputNumberComponent from "../../components/InputNumberComponent";
import MainButtonComponent from "../../components/MainButtonComponent";
import SwitchOptionComponent from "../../components/SwitchComponent";
import TableComponent, { DataTypeProps } from "../../components/TableComponent";
import { createColumn } from "../../utils/tableUtils";
import { GALLERY_ACCEPT, isGalleryVideoUrl } from "../../utils/galleryMedia";
import { useVolunteersGalleryContext } from "../../context/VolunteersGalleryContext/VolunteersGalleryContext";
import {
	VOLUNTEERS_GALLERY_LIST_PATH,
	VolunteersGalleryContextType,
	volunteersGalleryContextTypeLabels,
	VolunteersGalleryItem,
} from "../../context/VolunteersGalleryContext/constants";
import { ItemThumbnail, ItemVideoBadge, UploadActions } from "./styles";

const GENERAL_SECTION_KEY = "general";
const ITEMS_SECTION_KEY = "items";

type GalleryItemRow = VolunteersGalleryItem & { kind: string; isVideo: boolean };

// "3. görsel" / "3. video", for the confirmations and the screen reader labels.
const describeItem = (item: GalleryItemRow) => `${item.displayOrder}. ${item.isVideo ? "video" : "görsel"}`;

const EditVolunteersGallery = () => {
	const { id } = useParams<{ id: string }>();
	const {
		updateVolunteersGallery,
		getVolunteersGalleryDetails,
		clearVolunteersGalleryFormData,
		volunteersGalleryDetail,
		volunteersGalleryEditForm,
		setVolunteersGalleryEditForm,
		uploadVolunteersGalleryItems,
		updateVolunteersGalleryItem,
		removeVolunteersGalleryItem,
		getGalleryName,
		operationStatus,
		uploadProgress,
	} = useVolunteersGalleryContext();

	const [newFiles, setNewFiles] = useState<File[]>([]);
	const [orderItem, setOrderItem] = useState<GalleryItemRow | null>(null);
	const [newOrder, setNewOrder] = useState<number | undefined>();
	const [deleteItem, setDeleteItem] = useState<GalleryItemRow | null>(null);
	const replaceInputRef = useRef<HTMLInputElement>(null);
	const replaceItemRef = useRef<GalleryItemRow | null>(null);

	const isLoading = (operation: string) => operationStatus.type === "loading" && operationStatus.operation === operation;
	const isBusy = operationStatus.type === "loading";

	useEffect(() => {
		if (id) {
			getVolunteersGalleryDetails(id);
		}

		return () => {
			clearVolunteersGalleryFormData();
		};
	}, [id, getVolunteersGalleryDetails, clearVolunteersGalleryFormData]);

	const rows = useMemo<GalleryItemRow[]>(
		() => (volunteersGalleryDetail?.items ?? []).map(item => {
			const isVideo = isGalleryVideoUrl(item.image);
			return { ...item, isVideo, kind: isVideo ? "Video" : "Görsel" };
		}),
		[volunteersGalleryDetail]
	);

	const hasActiveItem = rows.some(row => row.isActive);

	const openOrderModal = useCallback((item: GalleryItemRow) => {
		setOrderItem(item);
		setNewOrder(item.displayOrder);
	}, []);

	const saveOrder = async () => {
		if (!orderItem) {
			return;
		}
		if (newOrder === undefined || !Number.isInteger(newOrder) || newOrder < 1 || newOrder > rows.length) {
			message.info(`Lütfen 1 ile ${rows.length} arasında bir sıra numarası girin.`);
			return;
		}
		if (newOrder === orderItem.displayOrder) {
			setOrderItem(null);
			return;
		}
		await updateVolunteersGalleryItem({ itemId: orderItem.id, displayOrder: newOrder }, "Sıra numarası başarıyla güncellendi.");
		setOrderItem(null);
	};

	const pickReplacement = useCallback((item: GalleryItemRow) => {
		replaceItemRef.current = item;
		replaceInputRef.current?.click();
	}, []);

	const replaceFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		const item = replaceItemRef.current;
		// The same file can be picked again after a failed attempt.
		event.target.value = "";
		if (file && item) {
			await updateVolunteersGalleryItem({ itemId: item.id, image: file }, "Dosya başarıyla değiştirildi.");
		}
	};

	const uploadNewFiles = async () => {
		const notUploaded = await uploadVolunteersGalleryItems(newFiles);
		setNewFiles(notUploaded);
	};

	const saveGallery = () => {
		if (newFiles.length > 0) {
			message.info("Seçilen dosyalar henüz yüklenmedi. Önce \"Dosyaları Yükle\" butonuna basın ya da seçimi temizleyin.");
			return;
		}
		updateVolunteersGallery();
	};

	const columns = useMemo(() => [
		createColumn({
			title: "Sıra Numarası",
			dataIndex: "displayOrder",
			width: 150,
			sorterType: "numeric",
		}),
		createColumn({
			title: "Önizleme",
			dataIndex: "image",
			width: 120,
			render: (text, record) => {
				const item = record as unknown as GalleryItemRow;
				if (!item.image) {
					return "-";
				}
				return (
					<a href={item.image} target="_blank" rel="noopener noreferrer" aria-label={`${describeItem(item)} dosyasını yeni sekmede aç`}>
						<ItemThumbnail>
							{item.isVideo ? <video src={`${item.image}#t=0.1`} preload="metadata" muted aria-hidden="true" /> : <img src={item.image} alt="" loading="lazy" />}
							{item.isVideo && (
								<ItemVideoBadge aria-hidden="true">
									<CaretRightOutlined />
								</ItemVideoBadge>
							)}
						</ItemThumbnail>
					</a>
				);
			},
		}),
		createColumn({
			title: "Dosya Türü",
			dataIndex: "kind",
			sorterType: "string",
		}),
		createColumn({
			title: "Aktiflik Durumu",
			dataIndex: "isActive",
			width: 160,
			sorterType: "boolean",
			render: (text, record) => (
				<StatusCell $bool={record.isActive}>{record.isActive ? "Aktif" : "Pasif"}</StatusCell>
			),
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 50,
			render: (text, record) => {
				const item = record as unknown as GalleryItemRow;
				const menuItems = [
					{
						key: "order",
						label: "Sırasını Değiştir",
						onClick: () => openOrderModal(item),
					},
					{
						key: "status",
						label: item.isActive ? "Pasife Al" : "Aktife Al",
						onClick: () => updateVolunteersGalleryItem(
							{ itemId: item.id, isActive: !item.isActive },
							item.isActive ? "Galeri öğesi pasife alındı." : "Galeri öğesi aktife alındı."
						),
					},
					{
						key: "replace",
						label: "Dosyayı Değiştir",
						onClick: () => pickReplacement(item),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => setDeleteItem(item),
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]} disabled={isBusy}>
						<MoreOutlined aria-label={`${describeItem(item)} işlemleri`} />
					</Dropdown>
				);
			},
		}),
	], [isBusy, openOrderModal, pickReplacement, updateVolunteersGalleryItem]);

	const isEventGallery = volunteersGalleryDetail?.contextType === VolunteersGalleryContextType.Event;

	const items: CollapseProps["items"] = volunteersGalleryDetail && volunteersGalleryEditForm ? [
		{
			key: GENERAL_SECTION_KEY,
			label: "Genel Bilgiler",
			children: (
				<>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={12} md={8} lg={6}>
							<SwitchOptionComponent
								title="Aktiflik Durumu"
								helperText="Aktif olmayan galeri sitede gözükmez."
								value={volunteersGalleryEditForm.isActive}
								onChange={checked => setVolunteersGalleryEditForm(prev => (prev ? { ...prev, isActive: checked } : null))}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={24} md={12} lg={8}>
							<InputComponent title="Galeri Yeri" value={volunteersGalleryContextTypeLabels[volunteersGalleryDetail.contextType]} isDisabled />
						</Col>
						{isEventGallery && (
							<Col xs={24} sm={24} md={12} lg={16}>
								<InputComponent title="Proje" value={getGalleryName(volunteersGalleryDetail)} isDisabled />
							</Col>
						)}
					</Row>
				</>
			),
		},
		{
			key: ITEMS_SECTION_KEY,
			label: `Galeri Öğeleri (${rows.length})`,
			children: (
				<>
					{!hasActiveItem && (
						<InfoMessageBox style={{ marginTop: 0, marginBottom: 16 }}>
							Galeride aktif görsel ya da video olmadığı için galeri sitede gözükmez.
						</InfoMessageBox>
					)}
					{/* As wide as the section: the table's default 885px minimum would push the action column out of the
					    narrower section on 1024-1440px screens. */}
					<TableComponent
						columns={columns}
						dataSource={rows as unknown as DataTypeProps[]}
						rowKey="id"
						maxWidth="100%"
						notFoundText="Galeride henüz görsel ya da video yok."
					/>
					<div style={{ marginTop: 24 }}>
						<GalleryMediaPicker
							title="Yeni Dosya Ekle"
							files={newFiles}
							onChange={setNewFiles}
							isDisabled={isBusy}
							uploadProgress={uploadProgress}
						/>
					</div>
					<UploadActions>
						<Button
							icon={<UploadOutlined />}
							size="large"
							onClick={uploadNewFiles}
							disabled={newFiles.length === 0 || (isBusy && !isLoading("upload"))}
							loading={isLoading("upload")}
						>
							Dosyaları Yükle
						</Button>
					</UploadActions>
				</>
			),
		},
	] : [];

	return (
		<>
			<BackComponent text="Galeri Listesine Dön" to={VOLUNTEERS_GALLERY_LIST_PATH} />
			<ActionContainer>
				<Spin spinning={isLoading("fetch") || isLoading("item")}>
					<ActionItemsContainer>
						<FormHeaderComponent>Galeri Güncelleme</FormHeaderComponent>
						{items.length > 0 && (
							<Collapse
								items={items}
								expandIconPosition="end"
								bordered={false}
								ghost
								expandIcon={CollapseExpandIcon}
								defaultActiveKey={[GENERAL_SECTION_KEY, ITEMS_SECTION_KEY]}
							/>
						)}
						<SubmitButtonOverlay>
							<MainButtonComponent
								onClick={saveGallery}
								type="default"
								disabled={!volunteersGalleryEditForm || (isBusy && !isLoading("update"))}
								loading={isLoading("update")}
							>
								Güncelle
							</MainButtonComponent>
						</SubmitButtonOverlay>
					</ActionItemsContainer>
				</Spin>
			</ActionContainer>
			<input ref={replaceInputRef} type="file" accept={GALLERY_ACCEPT} hidden onChange={replaceFile} aria-hidden="true" tabIndex={-1} />
			<Modal
				title="Sırasını Değiştir"
				open={orderItem !== null}
				onCancel={() => setOrderItem(null)}
				centered
				destroyOnClose
				footer={[
					<Button key="back" type="text" onClick={() => setOrderItem(null)}>
						İptal
					</Button>,
					<Button key="submit" type="primary" loading={isLoading("item")} onClick={saveOrder}>
						Kaydet
					</Button>,
				]}
			>
				<p>
					<strong>{orderItem ? describeItem(orderItem) : ""}</strong> için yeni sıra numarasını girin (1 - {rows.length}). Aradaki öğeler kendiliğinden kayar.
				</p>
				<InputNumberComponent
					title="Sıra Numarası"
					positiveOnly
					value={newOrder}
					onChange={value => setNewOrder(value === null || value === "" ? undefined : Number(value))}
				/>
			</Modal>
			<Modal
				title="Galeri Öğesini Sil"
				open={deleteItem !== null}
				onCancel={() => setDeleteItem(null)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setDeleteItem(null)}>
						İptal
					</Button>,
					<Button
						key="submit"
						type="primary"
						danger
						loading={isLoading("item")}
						onClick={async () => {
							if (deleteItem) {
								await removeVolunteersGalleryItem(deleteItem.id);
							}
							setDeleteItem(null);
						}}
					>
						Evet
					</Button>,
				]}
			>
				<p>
					<strong>{deleteItem ? describeItem(deleteItem) : ""}</strong> dosyasını galeriden silmek istediğinizden emin misiniz?
				</p>
			</Modal>
		</>
	);
};

export default EditVolunteersGallery;
